import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import AppService from "../../../../services/AppService";
import {
  getMockTransactions,
  formatCurrency,
} from "../../../../utils/mockWalletData";
import CustomerTopupHistory from "./tabs/CustomerTopupHistory";
import PartnerPaymentHistory from "./tabs/PartnerPaymentHistory";
import CommissionPaymentHistory from "./tabs/CommissionPaymentHistory";

/**
 * TabPanel Component
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`history-tabpanel-${index}`}
      aria-labelledby={`history-tab-${index}`}
      style={{ width: "100%", height: "100%" }}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2, height: "100%" }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `history-tab-${index}`,
    "aria-controls": `history-tabpanel-${index}`,
  };
}

/**
 * TransactionHistoryPage - Transaction history with 3 tabs
 *
 * Three tabs:
 * 1. Customer Top-up History (Money IN)
 * 2. Partner Payment History (Money OUT)
 * 3. Commission Payment History (Money OUT)
 *
 * Access: /wallet/transaction-history
 */
const TransactionHistoryPage = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [transactions, setTransactions] = useState(null);

  useEffect(() => {
    dispatch(updateScreenTitle("Transaction History"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  // Load all transactions from API (with fallback to mock data)
  useEffect(() => {
    const loadTransactions = async () => {
      try {
        console.log('[TransactionHistory] Loading all transactions from API...');

        // Load customer transactions (IN) - topups from customers
        console.log('[TransactionHistory] Loading CUSTOMER transactions...');
        const customerResponse = await AppService.get('/v1/transactions?type=CUSTOMER');

        // Load partner transactions (OUT) - payments to service partners
        console.log('[TransactionHistory] Loading PARTNER transactions...');
        const partnerResponse = await AppService.get('/v1/transactions?type=PARTNER');

        // Extract customer data
        let customerData = [];
        if (customerResponse && customerResponse.data && customerResponse.data.data && Array.isArray(customerResponse.data.data)) {
          customerData = customerResponse.data.data;
        } else if (customerResponse && customerResponse.data && Array.isArray(customerResponse.data)) {
          customerData = customerResponse.data;
        } else if (customerResponse && Array.isArray(customerResponse)) {
          customerData = customerResponse;
        }
        console.log('[TransactionHistory] Loaded', customerData.length, 'CUSTOMER transactions');

        // Extract partner data
        let partnerData = [];
        if (partnerResponse && partnerResponse.data && partnerResponse.data.data && Array.isArray(partnerResponse.data.data)) {
          partnerData = partnerResponse.data.data;
        } else if (partnerResponse && partnerResponse.data && Array.isArray(partnerResponse.data)) {
          partnerData = partnerResponse.data;
        } else if (partnerResponse && Array.isArray(partnerResponse)) {
          partnerData = partnerResponse;
        }
        console.log('[TransactionHistory] Loaded', partnerData.length, 'PARTNER transactions');

        // Transform customer transactions
        const transformedCustomer = customerData.map(t => ({
          id: t.id || t.transaction_id,
          transactionId: t.transaction_id,
          referenceNumber: t.reference_number,
          amount: t.amount,
          status: t.status,
          date: t.requested_at || t.created_at,
          paymentMethod: t.source_type || 'MANUAL',
          from: {
            name: t.organisation_name || 'Unknown',
            type: 'Customer Organization'
          },
          to: {
            name: 'Thaklis Platform',
            type: 'Platform'
          },
          notes: `Payment via ${t.payment_method || 'Bank Transfer'}${t.customer_payment_method ? ` - ${t.customer_payment_method}` : ''}`,
          type: 'CUSTOMER_TOPUP'
        }));

        // Transform partner transactions
        const transformedPartner = partnerData.map(t => ({
          id: t.id || t.transaction_id,
          transactionId: t.transaction_id,
          referenceNumber: t.reference_number,
          amount: t.amount,
          status: t.status,
          date: t.requested_at || t.created_at,
          paymentMethod: t.payment_method || 'BANK_TRANSFER',
          from: {
            name: 'Thaklis Platform',
            type: 'Platform'
          },
          to: {
            name: t.organisation_name || 'Unknown Partner',
            type: 'Partner Organization'
          },
          notes: `Payment to partner via ${t.payment_method || 'Bank Transfer'}`,
          type: 'PARTNER_PAYMENT'
        }));

        // Combine and set all transactions
        const allTransactions = {
          customer: transformedCustomer,
          partner: transformedPartner
        };

        setTransactions(allTransactions);
        console.log('[TransactionHistory] Total loaded:', transformedCustomer.length + transformedPartner.length, 'transactions');

      } catch (error) {
        console.error('[TransactionHistory] Error loading transactions from API:', error);
        console.log('[TransactionHistory] Error loading transactions, showing empty state');
        setTransactions({ customer: [], partner: [] });
      }
    };

    loadTransactions();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (!transactions) {
    return null;
  }

  // Separate transactions by type
  const topupTransactions = transactions.customer || [];
  const partnerPayments = transactions.partner || [];
  const commissionPayments = []; // TODO: Load from /v1/transactions?type=COMMISSION when implemented

  // Calculate totals
  const topupTotal = topupTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  const partnerTotal = partnerPayments.reduce((sum, t) => sum + (t.amount || 0), 0);
  const commissionTotal = commissionPayments.reduce((sum, t) => sum + (t.amount || 0), 0);
  const netTotal = topupTotal - partnerTotal - commissionTotal;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header Section - Simple */}
      <Paper sx={{ p: 2.5, mb: 3, bgcolor: "primary.main", color: "white" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Transaction History
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.95 }}>
              {(topupTransactions.length + partnerPayments.length + commissionPayments.length)} transactions
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="caption" sx={{ opacity: 0.9, display: "block" }}>
              Net Total
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {formatCurrency(netTotal)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Combined Breakdown + Tabs Section */}
      <Paper sx={{ mb: 3, overflow: "hidden" }}>
        {/* Breakdown Cards in Grid */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 0,
          p: 2,
          bgcolor: "#f5f5f5"
        }}>
          {/* From Customers (IN) - Clickable */}
          <Box
            onClick={() => handleTabChange(null, 0)}
            sx={{
              p: "16px",
              bgcolor: tabValue === 0 ? "#00897B" : "#00897B",
              color: "white",
              borderRadius: "8px 8px 0 0",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 137, 123, 0.3)",
              }
            }}>
            <Typography variant="caption" sx={{ fontWeight: "bold", color: "#B2DFDB", display: "block", mb: 0.3 }}>
              FROM CUSTOMERS
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.3 }}>
              {formatCurrency(topupTotal)}
            </Typography>
            <Typography variant="caption" sx={{ color: "#B2DFDB" }}>
              {topupTransactions.length} transactions
            </Typography>
          </Box>

          {/* To Partners (OUT) - Clickable */}
          <Box
            onClick={() => handleTabChange(null, 1)}
            sx={{
              p: "16px",
              bgcolor: tabValue === 1 ? "#00695C" : "#00695C",
              color: "white",
              borderRadius: "8px 8px 0 0",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 105, 92, 0.3)",
              }
            }}>
            <Typography variant="caption" sx={{ fontWeight: "bold", color: "#B2DFDB", display: "block", mb: 0.3 }}>
              TO SERVICE PARTNERS
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.3 }}>
              {formatCurrency(partnerTotal)}
            </Typography>
            <Typography variant="caption" sx={{ color: "#B2DFDB" }}>
              {partnerPayments.length} transactions
            </Typography>
          </Box>

          {/* To Channel Partners (OUT) - Clickable */}
          <Box
            onClick={() => handleTabChange(null, 2)}
            sx={{
              p: "16px",
              bgcolor: tabValue === 2 ? "#004D40" : "#004D40",
              color: "white",
              borderRadius: "8px 8px 0 0",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 77, 64, 0.3)",
              }
            }}>
            <Typography variant="caption" sx={{ fontWeight: "bold", color: "#B2DFDB", display: "block", mb: 0.3 }}>
              TO CHANNEL PARTNERS
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.3 }}>
              {formatCurrency(commissionTotal)}
            </Typography>
            <Typography variant="caption" sx={{ color: "#B2DFDB" }}>
              {commissionPayments.length} transactions
            </Typography>
          </Box>
        </Box>

        {/* Tabs Navigation */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="transaction history tabs"
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            bgcolor: "white",
            "& .MuiTab-root": {
              fontWeight: "bold",
              fontSize: "0.9rem",
              textTransform: "none",
              minHeight: "56px",
              color: "#666",
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: "#f9f9f9",
                color: "primary.main",
              },
              "&.Mui-selected": {
                color: "primary.main",
              }
            },
            "& .MuiTabs-indicator": {
              height: "3px",
              backgroundColor: "primary.main",
            }
          }}
        >
          <Tab
            label="Customer Top-ups (IN)"
            {...a11yProps(0)}
          />
          <Tab
            label="Partner Payments (OUT)"
            {...a11yProps(1)}
          />
          <Tab
            label="Commission Payments (OUT)"
            {...a11yProps(2)}
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ flexGrow: 1 }}>
        <TabPanel value={tabValue} index={0}>
          <CustomerTopupHistory
            data={{
              items: topupTransactions,
              total: topupTotal,
              count: topupTransactions.length
            }}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <PartnerPaymentHistory
            data={{
              items: partnerPayments,
              total: partnerTotal,
              count: partnerPayments.length
            }}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <CommissionPaymentHistory
            data={{
              items: commissionPayments,
              total: commissionTotal,
              count: commissionPayments.length
            }}
          />
        </TabPanel>
      </Box>
    </Container>
  );
};

export default TransactionHistoryPage;
